import { useEffect, useMemo, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Alert, Button, Col, Row, Segmented, Skeleton, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { trainingAPI } from "@api/TrainingAPI";
import { audioMessage } from "@components/Common/Notifications/audioMessage";
import "@styles/styles-PlatformUsage.css";

dayjs.extend(relativeTime);

interface UsageValue {
  value: number;
  prior: number;
}

interface UsageRange {
  bucket: "week" | "month";
  labels: string[];
  weekly_active_pct: UsageValue & { series: number[] };
  weekly_active_staff: { series: number[] };
  reports: UsageValue & { manual: number; automatic: number; series: number[] };
  data_points: UsageValue & {
    by_table: Record<string, number>;
    cumulative: number[];
    as_of: string[]; // The last day of each bucket that `cumulative` counts up to. The last one is today.
  };
  top_actions: { action: string; count: number }[];
}

type RangeKey = "1M" | "6M" | "1Y";

interface PlatformUsagePayload {
  staff_accounts: number;
  tracked_since: string | null;
  synced_at: string | null;
  ranges: Record<RangeKey, UsageRange>;
}

type RefreshResponse = PlatformUsagePayload & { refreshed: boolean };

const RANGE_KEYS: RangeKey[] = ["1M", "6M", "1Y"];

// The words that the tiles use for the selected range and for its prior period.
const PERIOD_WORDS: Record<RangeKey, { current: string; prior: string }> = {
  "1M": { current: "the last month", prior: "the previous month" },
  "6M": { current: "the last 6 months", prior: "the previous 6 months" },
  "1Y": { current: "the last 12 months", prior: "the previous 12 months" },
};

// Highcharts cannot read CSS variables. These values match the variables in styles-PlatformUsage.css.
const CHART_COLORS = {
  orange: "#E8562B",
  orangeDim: "#F3A48D",
  slate: "#6B6862",
  slateLight: "#98948C",
  hairline: "#E5E1D8",
  ink: "#15130F",
  panel: "#FFFFFF",
};

const countFormat = new Intl.NumberFormat("en-US");
const compactFormat = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

// `asOf` gives a point-in-time chart its dates: each point is the total through that day.
const bucketTitles = (labels: string[], bucket: UsageRange["bucket"], asOf?: string[]) => {
  const through = asOf?.map((day) => `Through ${dayjs(day).format("MMM D, YYYY")}`);
  if (bucket === "week") {
    return {
      axis: (asOf ?? labels).map((day) => dayjs(day).format("MMM D")),
      tooltip:
        through ??
        labels.map((label) => {
          const start = dayjs(label);
          return `${start.format("MMM D")} – ${start.add(6, "day").format("MMM D")}`;
        }),
    };
  }
  const crossesYear = dayjs(labels[0]).year() !== dayjs(labels[labels.length - 1]).year();
  const months = labels.map((label) => dayjs(label).format(crossesYear ? "MMM 'YY" : "MMM"));
  return { axis: months, tooltip: through ?? months };
};

const Delta = ({ diff, format }: { diff: number; format: (amount: number) => string }) => {
  if (diff === 0) return <span className="platform-usage-delta platform-usage-delta-flat">No change</span>;
  const isUp = diff > 0;
  return (
    <span className={`platform-usage-delta platform-usage-delta-${isUp ? "up" : "down"}`}>
      {isUp ? "▲" : "▼"} {format(Math.abs(diff))}
    </span>
  );
};

interface StatTileProps {
  label: string;
  value: string;
  isTextValue?: boolean;
  context: string;
  badge?: ReactNode;
  footnote?: string;
}

const StatTile = ({ label, value, isTextValue, context, badge, footnote }: StatTileProps) => (
  <div className="platform-usage-tile">
    <div className="platform-usage-tile-label">{label}</div>
    <div className="platform-usage-tile-value">
      <span className={isTextValue ? "platform-usage-tile-text" : "platform-usage-tile-num"} title={isTextValue ? value : undefined}>
        {value}
      </span>
    </div>
    <div className="platform-usage-tile-context">{context}</div>
    {(badge || footnote) && (
      <div className="platform-usage-tile-footer">
        {badge}
        {footnote && <span>{footnote}</span>}
      </div>
    )}
  </div>
);

const Section = ({ title, note, children }: { title: string; note: string; children: ReactNode }) => (
  <div>
    <div className="platform-usage-section-head">
      <h3 className="platform-usage-section-title">{title}</h3>
      <span className="platform-usage-section-note">{note}</span>
    </div>
    {children}
  </div>
);

interface UsageChartProps {
  type?: "areaspline" | "step" | "column";
  seriesName: string;
  labels: string[];
  bucket: UsageRange["bucket"];
  data: number[];
  asOf?: string[];
  isPercent?: boolean;
}

const AXIS_LABEL_STYLE = { color: CHART_COLORS.slateLight, fontSize: "10.5px", textOverflow: "none" };
const DATA_LABEL_STYLE = { color: CHART_COLORS.ink, fontSize: "11px", fontWeight: "600", textOutline: "none" };
const LINE_OPTIONS = { lineWidth: 2.5, fillOpacity: 0.1, marker: { radius: 3.5 }, pointPlacement: "on", clip: false } as const;

const UsageChart = ({ type = "areaspline", seriesName, labels, bucket, data, asOf, isPercent }: UsageChartProps) => {
  const options = useMemo<Highcharts.Options>(() => {
    const titles = bucketTitles(labels, bucket, asOf);
    const lastIndex = data.length - 1;
    const isColumn = type === "column";
    const isStep = type === "step";
    const formatValue = (value: number) => (isPercent ? `${countFormat.format(value)}%` : compactFormat.format(value));
    // The last bucket is still in progress: the zones draw its column light, or its line segment dashed.
    // A step segment starts at its own point, a curve segment at the previous point.
    const zones: Highcharts.SeriesZonesOptionsObject[] = isColumn
      ? [{ value: lastIndex }, { color: CHART_COLORS.orangeDim }]
      : [{ value: isStep ? lastIndex : lastIndex - 1 }, { dashStyle: "Dash" }];
    // A "step" chart holds each bucket's value flat from its label to the next label. The last bucket has no next
    // label, so one hidden point at the right edge closes its step.
    const seriesData = isStep
      ? [...data, { y: data[lastIndex], marker: { enabled: false }, dataLabels: { enabled: false } }]
      : data;
    const seriesType = isStep ? { type: "area", step: "left" } : { type };
    const series = [{ ...seriesType, name: seriesName, data: seriesData, zoneAxis: "x", zones }] as Highcharts.SeriesOptionsType[];

    return {
      chart: { height: 240, spacingRight: 20, backgroundColor: "transparent", style: { fontFamily: "inherit" } },
      colors: [CHART_COLORS.orange],
      title: { text: undefined },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: isStep ? [...titles.axis, ""] : titles.axis,
        lineColor: CHART_COLORS.hairline,
        gridLineWidth: 1,
        gridLineColor: CHART_COLORS.hairline,
        tickLength: 0,
        // A line starts and ends on the first and last bucket, with no half-bucket margin. Columns keep the margin.
        ...(isColumn ? {} : { tickmarkPlacement: "on" as const, min: 0, max: isStep ? lastIndex + 1 : lastIndex }),
        labels: { style: AXIS_LABEL_STYLE },
      },
      yAxis: {
        title: { text: undefined },
        min: 0,
        // The axis fits the data. A percent axis never goes past 100%.
        ...(isPercent ? { ceiling: 100, labels: { format: "{value}%", style: AXIS_LABEL_STYLE } } : { labels: { style: AXIS_LABEL_STYLE } }),
        allowDecimals: false,
        gridLineWidth: 1,
        gridLineColor: CHART_COLORS.hairline,
      },
      tooltip: {
        shared: true,
        backgroundColor: CHART_COLORS.panel,
        borderColor: CHART_COLORS.hairline,
        shadow: false,
        style: { color: CHART_COLORS.ink },
        formatter() {
          const points = this.points ?? [];
          // The hidden closing point of a step chart shows the last bucket.
          const index = Math.min(points[0]?.point.index ?? 0, lastIndex);
          const header = `${titles.tooltip[index]}${index === lastIndex ? " (in progress)" : ""}`;
          const rows = points.map((point) => `${point.series.name}: <b>${formatValue(point.y ?? 0)}</b>`);
          return [header, ...rows].join("<br/>");
        },
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            formatter() {
              return formatValue(this.y ?? 0);
            },
            style: DATA_LABEL_STYLE,
          },
        },
        areaspline: LINE_OPTIONS,
        area: LINE_OPTIONS,
        column: { borderWidth: 0, borderRadius: 2, pointPadding: 0.04, groupPadding: 0.08 },
      },
      series,
    };
  }, [type, seriesName, labels, bucket, data, asOf, isPercent]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export const PlatformUsage = () => {
  const [payload, setPayload] = useState<PlatformUsagePayload | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [rangeKey, setRangeKey] = useState<RangeKey>("6M");

  useEffect(() => {
    trainingAPI
      .getPlatformUsage()
      .then(setPayload)
      .catch((err: unknown) => {
        console.error("Error fetching platform usage:", err);
        audioMessage.error("Could not load the usage data.");
        setLoadFailed(true);
      });
  }, []);

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      const { refreshed, ...fresh }: RefreshResponse = await trainingAPI.refreshPlatformUsage();
      setPayload(fresh);
      if (refreshed) audioMessage.success("Usage data refreshed.");
      else audioMessage.info("A refresh already ran in the last 5 minutes. Try again later.");
    } catch (err) {
      console.error("Error refreshing platform usage:", err);
      audioMessage.error("Could not refresh. The page shows the data from the last sync.");
    } finally {
      setIsRefreshing(false);
    }
  };

  if (loadFailed) {
    return <Alert type="error" showIcon message="Could not load the usage data." description="Reload the page to try again." />;
  }
  if (!payload) return <Skeleton active paragraph={{ rows: 8 }} />;

  const range = payload.ranges[rangeKey];
  const {
    weekly_active_pct: active,
    weekly_active_staff: activeStaff,
    reports,
    data_points: dataPoints,
    top_actions: topActions,
  } = range;
  const topAction = topActions[0];
  const period = PERIOD_WORDS[rangeKey];
  const syncedAt = payload.synced_at ? dayjs(payload.synced_at) : null;
  const showTrackedNote = payload.tracked_since !== null && dayjs(range.labels[0]).isBefore(payload.tracked_since);
  const chartProps = { labels: range.labels, bucket: range.bucket };
  const charts = [
    {
      title: "Staff engagement over time",
      note: "Average share of your staff accounts that were active each week",
      props: {
        type: "column",
        seriesName: "Active staff",
        isPercent: true,
        // The percent comes from the weekly active-staff counts and today's staff count. It is 0 when the team has no staff accounts.
        data: activeStaff.series.map((count) =>
          payload.staff_accounts ? Math.round((100 * count) / payload.staff_accounts) : 0,
        ),
      },
    },
    {
      title: "Reports generated over time",
      note: "The manual and automatic reports that your team generated in each week or month.",
      props: { type: "step", seriesName: "Reports", data: reports.series },
    },
    {
      title: "Data volume over time",
      note: "All the data points your team has captured, up to the end of each week or month.",
      props: { seriesName: "Data points", data: dataPoints.cumulative, asOf: dataPoints.as_of },
    },
  ] as const;

  return (
    <div className="platform-usage">
      <div className="platform-usage-header">
        <Segmented options={RANGE_KEYS} value={rangeKey} onChange={(value) => setRangeKey(value as RangeKey)} />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StatTile
            label="Weekly active staff"
            value={`${active.value}%`}
            context={`of ${payload.staff_accounts} staff accounts in an average week`}
            badge={<Delta diff={active.value - active.prior} format={(amount) => `${amount} pts`} />}
            footnote={`vs. ${period.prior}`}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatTile
            label="Reports generated"
            value={countFormat.format(reports.value)}
            context={`Reports generated for your team in ${period.current}`}
            badge={<Delta diff={reports.value - reports.prior} format={countFormat.format} />}
            footnote={`vs. ${period.prior}`}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatTile
            label="Data points captured"
            value={compactFormat.format(dataPoints.value)}
            context={`New data across all data streams in ${period.current}`}
            badge={<Delta diff={dataPoints.value - dataPoints.prior} format={compactFormat.format} />}
            footnote={`vs. ${period.prior}`}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatTile
            label="Most common action"
            value={topAction?.action ?? "No actions yet"}
            isTextValue
            context={`Your staff's top action in ${period.current}`}
            badge={
              topAction && (
                <span className="platform-usage-delta platform-usage-delta-flat">
                  {countFormat.format(topAction.count)} {topAction.count === 1 ? "time" : "times"}
                </span>
              )
            }
          />
        </Col>
      </Row>

      <Row gutter={[16, 28]} className="platform-usage-charts">
        {charts.map(({ title, note, props }) => (
          <Col key={title} span={24}>
            <Section title={title} note={note}>
              <div className="platform-usage-panel">
                <UsageChart {...chartProps} {...props} />
              </div>
            </Section>
          </Col>
        ))}
        <Col span={24}>
          <Section title="Top 5 actions" note="The actions your staff takes most often in this range.">
            <div className="platform-usage-rank-list">
              {topActions.length === 0 && <div className="platform-usage-rank-row platform-usage-rank-empty">No actions yet</div>}
              {topActions.map((item, index) => (
                <div key={item.action} className="platform-usage-rank-row">
                  <span className="platform-usage-rank-num">{index + 1}</span>
                  <span className="platform-usage-rank-name">{item.action}</span>
                  <span className="platform-usage-rank-value">
                    {countFormat.format(item.count)}
                    <span className="platform-usage-rank-unit">{item.count === 1 ? "time" : "times"}</span>
                  </span>
                </div>
              ))}
            </div>
          </Section>
        </Col>
      </Row>

      {showTrackedNote && (
        <div className="platform-usage-meta platform-usage-tracked">
          Usage tracking starts on {dayjs(payload.tracked_since).format("MMM D, YYYY")}. Earlier buckets show no activity.
        </div>
      )}

      <div className="platform-usage-footer">
        <div className="platform-usage-sync">
          <span className="platform-usage-meta" title={syncedAt?.format("MMM D, YYYY h:mm A")}>
            {syncedAt ? `Last synced ${dayjs(Math.min(Date.now(), syncedAt.valueOf())).fromNow()}` : "Not synced yet"}
          </span>
          <Tooltip title="Refresh now">
            <Button
              className="platform-usage-refresh"
              type="text"
              size="small"
              shape="circle"
              aria-label="Refresh now"
              icon={<ReloadOutlined />}
              loading={isRefreshing}
              onClick={refresh}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
